import React from "react";
import { Alert, Card, Descriptions, Space, Tag, Typography } from "antd";
import SolicitacaoReservaStatusTag, {
  type StatusSolicitacaoReservaFront,
} from "../components/SolicitacaoReservaStatusTag";
import type { ResumoEstadiaByTokenResponse } from "../../../services/solicitacaoReservaService";
import { formatDate } from "../../../uteis/dateUtils";

const { Paragraph, Text, Title } = Typography;

type Props = {
  variant: "solicitacao_invalida" | "pagamento_interrompido";
  message: string;
  resumo: ResumoEstadiaByTokenResponse | null;
  periodoProcuradoIni?: string | null;
  periodoProcuradoFim?: string | null;
  periodoEncontradoIni?: string | null;
  periodoEncontradoFim?: string | null;
};

const TITLES = {
  solicitacao_invalida: "Solicitação de reserva inválida",
  pagamento_interrompido: "Pagamento da taxa interrompido",
} as const;

const ALERT_META = {
  solicitacao_invalida: {
    type: "error" as const,
    tagColor: "red",
    tagLabel: "Solicitação inválida",
    helperText: "Esse fluxo foi encerrado e não seguirá para pagamento ou nova validação.",
  },
  pagamento_interrompido: {
    type: "warning" as const,
    tagColor: "orange",
    tagLabel: "Pagamento interrompido",
    helperText: "Sua solicitação segue em tratamento operacional enquanto buscamos outra possibilidade.",
  },
} as const;

export default function StepEstadoSolicitacao({
  variant,
  message,
  resumo,
  periodoProcuradoIni,
  periodoProcuradoFim,
  periodoEncontradoIni,
  periodoEncontradoFim,
}: Props) {
  const meta = ALERT_META[variant];
  const showStatusTag = variant === "pagamento_interrompido";
  const periodoProcurado =
    periodoProcuradoIni && periodoProcuradoFim
      ? `${formatDate(periodoProcuradoIni)} → ${formatDate(periodoProcuradoFim)}`
      : null;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div>
            {showStatusTag ? (
              <SolicitacaoReservaStatusTag
                status={"PAGAMENTO_TX_INTERROMPIDO" as StatusSolicitacaoReservaFront}
              />
            ) : (
              <Tag color={meta.tagColor} style={{ borderRadius: 999 }}>
                {meta.tagLabel}
              </Tag>
            )}
          </div>

          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              {TITLES[variant]}
            </Title>
            <Paragraph style={{ marginBottom: 0 }}>
              {message}
            </Paragraph>
          </div>

          <Alert
            type={meta.type}
            showIcon
            message={meta.tagLabel}
            description={meta.helperText}
            style={{ borderRadius: 14 }}
          />

          {(resumo || periodoProcurado) && (
            <Card
              bordered={false}
              style={{ borderRadius: 14, background: "rgba(0,0,0,0.02)" }}
              styles={{ body: { padding: 18 } }}
            >
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Title level={4} style={{ margin: 0 }}>
                  Dados da solicitação
                </Title>

                <Descriptions column={1} size="small" colon={false}>
                  {resumo?.empreendimento?.nome && (
                    <Descriptions.Item label="Empreendimento">
                      <Text strong>{resumo.empreendimento.nome}</Text>
                    </Descriptions.Item>
                  )}

                  {periodoProcurado && (
                    <Descriptions.Item label="Período procurado">
                      <Text strong>{periodoProcurado}</Text>
                    </Descriptions.Item>
                  )}

                  {periodoEncontradoIni && periodoEncontradoFim && (
                    <Descriptions.Item label="Período da proposta">
                      <Text strong>
                        {formatDate(periodoEncontradoIni)} → {formatDate(periodoEncontradoFim)}
                      </Text>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Space>
            </Card>
          )}
        </Space>
      </Card>
    </div>
  );
}
